import { useEffect, useState } from "react";
import { observer } from 'mobx-react';

import { Link } from 'react-router-dom';

import { format } from "date-fns";
import { de } from 'date-fns/locale';
import { toast } from "react-toastify";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import GooglePlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-google-places-autocomplete";

import kidsApi from "@api/kids.api";

import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import InputLabel from "@mui/material/InputLabel";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

import { hasToken } from "@helpers/token";

import COLORS from "@styles/theme/colors";
import * as commonS from "@styles/common";
import * as S from "./styles";

const KidsView = ({ project }) => {
  const [isButtonDisabled, setButtonDisabled] = useState(true);
  const [address, setAddress] = useState(null);
  const [kid, setKid] = useState({});
  const [isFormSubmitted, setFormSubmitted] = useState(false);

  useEffect(() => {
    if (address) {
      geocodeByAddress(address?.label)
        .then((results) => getLatLng(results[0]))
        .then(({ lat, lng }) =>
          setKid({
            ...kid,
            address: address?.label,
            location: {
              type: "Point",
              coordinates: [lng, lat],
            },
            placeId: address?.value?.place_id,
          })
        );
    }
  }, [address?.label]);

  const handleChange = ({ target }: { target: HTMLInputElement }) => {
    const { name, value, checked } = target;

    if (name === "previousExperience" || name === "schoolBus") {
      setKid({
        ...kid,
        [name]: checked,
      });
    } else {
      setKid({
        ...kid,
        [name]: value,
      });
    }
  };

  useEffect(() => {
    if (
      kid?.firstName &&
      kid?.lastName &&
      kid?.address &&
      kid?.gender &&
      kid?.germanLanguageLevel
    ) {
      setButtonDisabled(false);
    }
  }, [kid]);

  const handleAddNew = async () => {
    await kidsApi
      .addByQr({
        ...kid,
        birthday: kid?.birthday
          ? format(new Date(kid?.birthday), "dd. MMMM yyyy")
          : "",
        fullName: `${kid?.lastName} ${kid?.firstName}`,
        project: project,
      })
      .then((e: any) =>
        toast.success('SuS erfolgreich hinzugefügt', { autoClose: 3000 })
      );
    await setKid({});
    await setAddress(null);
    setButtonDisabled(true);
    setFormSubmitted(true);
  };

  return (
    <>
      <commonS.MainBody>
        <S.Wrap>
          {isFormSubmitted ?
            <>
              <h1>SuS erfolgreich hinzugefügt</h1>
              <S.ButtonRow>
                <Button variant='contained' color='primary' size='large' sx={{ minWidth: 205 }} onClick={() => setFormSubmitted(false)}>Weitere SuS hinzufügen</Button>
                {hasToken() && <Link to={`/projekte/${project}/sus`}>Back to SuS</Link>}
              </S.ButtonRow>
            </> :
            <>
              <S.FormItem>
                <InputLabel htmlFor="firstName" shrink={false}>
                  Vorname<span style={{ color: COLORS.red }}>*</span>
                </InputLabel>
                <TextField
                  value={kid.firstName}
                  onChange={handleChange}
                  variant="outlined"
                  name="firstName"
                  id="firstName"
                />
              </S.FormItem>

              <S.FormItem>
                <InputLabel htmlFor="lastName" shrink={false}>
                  Name<span style={{ color: COLORS.red }}>*</span>
                </InputLabel>
                <TextField
                  value={kid.lastName}
                  onChange={handleChange}
                  variant="outlined"
                  name="lastName"
                  id="lastName"
                />
              </S.FormItem>


              <S.FormItem>
                <InputLabel htmlFor="address" shrink={false}>
                  Adresse<span style={{ color: COLORS.red }}>*</span>
                </InputLabel>
                <GooglePlacesAutocomplete
                  apiOptions={{ language: "de", region: "ch" }}
                  autocompletionRequest={{
                    componentRestrictions: {
                      country: ["ch"],
                    },
                  }}
                  selectProps={{
                    value: address,
                    onChange: setAddress,
                    styles: {
                      input: (provided: any) => ({
                        ...provided,
                        color: "#778097",
                      }),
                      control: (provided: any, state: any) => ({
                        ...provided,
                        borderColor: state.isFocused
                          ? "#4F77DE"
                          : "rgba(102, 102, 102, 0.35)",
                        outline: "none",
                        boxShadow: "none",
                        borderRadius: 8,
                        padding: "2.5px 5px",
                      }),
                      option: (provided: any) => ({
                        ...provided,
                        color: "#778097",
                      }),
                      singleValue: (provided: any) => ({
                        ...provided,
                        color: "#778097",
                      }),
                    },
                  }}
                />
              </S.FormItem>

              <S.FormRow>
                <S.FormItem>
                  <InputLabel htmlFor="genderSelect" shrink={false}>
                    Geschlecht<span style={{ color: COLORS.red }}>*</span>
                  </InputLabel>
                  <Select
                    labelId="genderSelect"
                    id="gender"
                    label="genderSelect"
                    name="gender"
                    value={kid.gender}
                    onChange={handleChange}
                  >
                    <MenuItem value="male">Männlich</MenuItem>
                    <MenuItem value="female">Weiblich</MenuItem>
                  </Select>
                </S.FormItem>

                <S.FormItem>
                  <InputLabel htmlFor="birthdayLabel" shrink={false}>
                    Geburtstag
                  </InputLabel>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
                    <DesktopDatePicker
                      value={kid?.birthday}
                      inputFormat="dd. MMMM yyyy"
                      disableFuture
                      name="birthday"
                      inputProps={{
                        disabled: true,
                        readOnly: true,
                        name: "birthday",
                      }}
                      onChange={(e) => {
                        setKid({ ...kid, birthday: e });
                      }}
                      renderInput={(params) => (
                        <TextField name="birthday" disabled {...params} />
                      )}
                    />
                  </LocalizationProvider>
                </S.FormItem>
              </S.FormRow>
              <S.FormRow>
                <S.FormItem>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="previousExperience"
                        onChange={handleChange}
                        checked={
                          kid?.previousExperience
                        }
                      />
                    }
                    label="Erfahrung Betreuung"
                  />
                </S.FormItem>

                <S.FormItem>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="schoolBus"
                        onChange={handleChange}
                        checked={
                          kid.schoolBus
                        }
                      />
                    }
                    label="Schulbus"
                  />
                </S.FormItem>
              </S.FormRow>
              <S.FormItem>
                <InputLabel htmlFor="susId" shrink={false}>
                  SuS_ID
                </InputLabel>
                <TextField
                  id="susId"
                  onChange={handleChange}
                  value={kid.susId}
                  name="susId"
                  variant="outlined"
                />
              </S.FormItem>
              <S.FormRow>
                <S.FormItem>
                  <InputLabel htmlFor="germanLanguageLevel" shrink={false}>
                    Deutsch<span style={{ color: COLORS.red }}>*</span>
                  </InputLabel>
                  <Select
                    labelId="germanLanguageLevel"
                    id="germanLanguageLevel"
                    label="germanLanguageLevel"
                    name="germanLanguageLevel"
                    value={
                      kid.germanLanguageLevel
                    }
                    onChange={handleChange}
                  >
                    <MenuItem value="none">Gar kein</MenuItem>
                    <MenuItem value="little">Wenig</MenuItem>
                    <MenuItem value="medium">Mittel</MenuItem>
                    <MenuItem value="high">Viel</MenuItem>
                    <MenuItem value="fluent">Ausschliesslich</MenuItem>
                  </Select>
                </S.FormItem>
                <S.FormItem>
                  <InputLabel htmlFor="motherLanguage" shrink={false}>
                    Umgangssprache
                  </InputLabel>
                  <TextField
                    id="motherLanguage"
                    onChange={handleChange}
                    value={
                      kid.motherLanguage
                    }
                    name="motherLanguage"
                    variant="outlined"
                  />
                </S.FormItem>
              </S.FormRow>

              <S.FormItem>
                <InputLabel htmlFor="note" shrink={false}>
                  Notizen
                </InputLabel>
                <TextField
                  id="note"
                  onChange={handleChange}
                  value={kid.note}
                  name="note"
                  variant="outlined"
                />
              </S.FormItem>

              <S.FormItem>
                <InputLabel htmlFor="specialNote" shrink={false}>
                  Wunsch
                </InputLabel>
                <TextField
                  id="specialNote"
                  onChange={handleChange}
                  value={kid.specialNote}
                  name="specialNote"
                  variant="outlined"
                />
              </S.FormItem>
              <S.ButtonRow>
                <Button variant='contained' color='primary' disabled={isButtonDisabled} size='large' sx={{ minWidth: 205 }} onClick={handleAddNew}>SuS hinzufügen</Button>
              </S.ButtonRow>
            </>}
        </S.Wrap>
      </commonS.MainBody>
    </>
  );
};

export default observer(KidsView);
